import { useMemo, useState } from "react";
import useSWR from "swr";
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  Pill,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconCheck, IconUsers, IconX } from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { fetcher } from "../../../../services/fetcher";
import {
  explorePlansApi,
  parseCompetitorsResponse,
  parseComparisonsListResponse,
} from "../../services/explorePlansApi";
import storyClasses from "../../components/CoverStoryView/CoverStoryView.module.css";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";

const competitorItemId = (item) =>
  Number(item?.id ?? item?.pk ?? item?.competitor_id ?? item?.competitor);

const competitorDisplayName = (item) =>
  item?.name ??
  item?.title ??
  item?.label ??
  item?.full_name ??
  item?.display_name ??
  item?.competitor_name ??
  `Competitor ${Number.isFinite(competitorItemId(item)) ? competitorItemId(item) : "—"}`;

const COMPETITOR_REGISTRY_PATHS = [
  "cover-api/competitors/",
  "cover-api/competitors",
];

const fetchCompetitorsRegistry = async () => {
  let lastErr;
  for (const path of COMPETITOR_REGISTRY_PATHS) {
    try {
      return await fetcher(path);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      if (status === 404 || status === 405) continue;
      throw err;
    }
  }
  if (lastErr) throw lastErr;
  return [];
};

const enrichMetaFromApi = (meta, apiList) =>
  meta.map((m) => {
    const id = Number(m.competitor);
    if (!Number.isFinite(id) || id <= 0) return m;
    const hit = apiList.find((a) => competitorItemId(a) === id);
    return {
      ...m,
      label: m.label || (hit ? competitorDisplayName(hit) : ""),
    };
  });

const metaFromApiList = (apiList) =>
  (apiList || [])
    .map((c) => ({
      competitor: String(competitorItemId(c) || ""),
      label: competitorDisplayName(c),
    }))
    .filter((m) => Number(m.competitor) > 0);

const buildTemplateFromRows = (sortedRows) => {
  const order = [];
  const seen = new Set();
  const labelById = new Map();
  for (const row of sortedRows) {
    for (const cv of row.competitor_values || []) {
      const id = Number(cv.competitor);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (!seen.has(id)) {
        seen.add(id);
        order.push(id);
        labelById.set(
          id,
          cv.competitor_name || cv.competitor_slug || `Competitor ${id}`
        );
      }
    }
  }
  return order.map((id) => ({
    competitor: String(id),
    label: labelById.get(id) || "",
  }));
};

/** Keeps column count when ids are missing but cells exist (legacy / draft rows). */
const fallbackMetaFromRows = (sortedRows) => {
  for (const row of sortedRows) {
    const list = row.competitor_values || [];
    if (list.length) {
      return list.map((cv) => ({
        competitor:
          cv.competitor != null ? String(cv.competitor) : "",
        label: cv.competitor_name || cv.competitor_slug || "",
      }));
    }
  }
  return [];
};

const alignRowValuesToMeta = (meta, rowValues) =>
  meta.map((m, i) => {
    const id = Number(m.competitor);
    const byId = new Map(
      (rowValues || []).map((cv) => [Number(cv.competitor), cv])
    );
    const hitById =
      Number.isFinite(id) && id > 0 ? byId.get(id) : null;
    const hitPos = rowValues?.[i];
    const hit = hitById || hitPos;
    return {
      competitor: String(m.competitor || hit?.competitor || ""),
      value: hit?.value || "",
      is_negative: !!hit?.is_negative,
    };
  });

const emptyCoverageRow = (order, meta) => ({
  coverage_label: "",
  asc360_value: "",
  asc360_is_winner: true,
  order,
  competitor_values: alignRowValuesToMeta(meta, []),
});

const toFormDynamic = (row) => ({
  coverage_label: row.coverage_label || "",
  asc360_value: row.asc360_value || "",
  asc360_is_winner: !!row.asc360_is_winner,
  order: Number(row.order ?? 0),
  competitor_values:
    (row.competitor_values || []).length > 0
      ? (row.competitor_values || []).map((cv) => ({
          competitor: cv.competitor != null ? String(cv.competitor) : "",
          label: cv.competitor_name || cv.competitor_slug || "",
          value: cv.value || "",
          is_negative: !!cv.is_negative,
        }))
      : [
          {
            competitor: "",
            label: "",
            value: "",
            is_negative: false,
          },
        ],
});

const toFormForBulk = (row, meta) => ({
  id: row.id,
  ...toFormDynamic(row),
  competitor_values: alignRowValuesToMeta(meta, row.competitor_values),
});

const modalSelectOptions = (entries, colIdx, apiList) => {
  const current = Number(entries[colIdx]?.competitor);
  const opts = apiList
    .filter((item) => {
      const id = competitorItemId(item);
      if (!Number.isFinite(id) || id <= 0) return false;
      if (id === current) return true;
      return !entries.some(
        (e, j) => j !== colIdx && Number(e.competitor) === id
      );
    })
    .map((item) => ({
      value: String(competitorItemId(item)),
      label: competitorDisplayName(item),
    }));
  const v = entries[colIdx]?.competitor;
  if (v && !opts.some((o) => o.value === String(v))) {
    opts.unshift({
      value: String(v),
      label: entries[colIdx]?.label || `id ${v}`,
    });
  }
  return opts;
};

const ModalCompetitorBlock = ({
  entries,
  setEntries,
  apiList,
  registryLoading,
  registryError,
  onRetryRegistry,
}) => {
  const addFromApi = () => {
    setEntries((prev) => {
      const used = new Set(
        prev.map((e) => Number(e.competitor)).filter(Number.isFinite)
      );
      const pick = apiList.find((c) => {
        const id = competitorItemId(c);
        return Number.isFinite(id) && id > 0 && !used.has(id);
      });
      if (!pick) return prev;
      const id = String(competitorItemId(pick));
      return [
        ...prev,
        {
          competitor: id,
          label: competitorDisplayName(pick),
          value: "",
          is_negative: false,
        },
      ];
    });
  };

  return (
    <Box
      mt="md"
      p="sm"
      bg="gray.0"
      style={{ border: "1px solid #e2e8f0", borderRadius: 8 }}
    >
      <Group justify="space-between" mb="sm">
        <Group gap={8}>
          <ThemeIcon size="sm" variant="light" color="blue" radius="md">
            <IconUsers size={14} />
          </ThemeIcon>
          <Text size="sm" fw={600}>
            Competitor values
          </Text>
        </Group>
        <Button
          size="xs"
          variant="light"
          onClick={addFromApi}
          disabled={!apiList.length}
        >
          + Add competitor
        </Button>
      </Group>
      {registryLoading && !apiList.length ? (
        <Group gap="xs" py="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading competitor directory…
          </Text>
        </Group>
      ) : null}
      {registryError && !apiList.length ? (
        <Alert color="orange" size="sm" mb="sm">
          <Text size="sm">
            Competitor directory could not be loaded. Competitors must be
            chosen from the API list.
          </Text>
          {onRetryRegistry ? (
            <Button mt="xs" size="xs" variant="light" onClick={onRetryRegistry}>
              Retry
            </Button>
          ) : null}
        </Alert>
      ) : null}
      {!registryLoading && !apiList.length && !registryError ? (
        <Text size="sm" c="dimmed" mb="sm">
          No competitors returned from the API.
        </Text>
      ) : null}
      <Stack gap="sm">
        {entries.map((cv, i) => (
          <Paper key={i} p="sm" withBorder radius="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <Select
                label="Competitor"
                placeholder={
                  apiList.length ? "Select competitor" : "Waiting for API…"
                }
                searchable
                clearable
                disabled={!apiList.length}
                data={modalSelectOptions(entries, i, apiList)}
                value={cv.competitor || null}
                onChange={(v) => {
                  const id = v || "";
                  const hit = apiList.find(
                    (a) => competitorItemId(a) === Number(id)
                  );
                  setEntries((prev) => {
                    const next = [...prev];
                    next[i] = {
                      ...next[i],
                      competitor: id,
                      label: hit ? competitorDisplayName(hit) : "",
                    };
                    return next;
                  });
                }}
              />
              <TextInput
                label="Their coverage"
                placeholder="Their coverage"
                value={cv.value || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setEntries((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i], value: v };
                    return next;
                  });
                }}
              />
            </SimpleGrid>
            <Group justify="flex-end" mt="xs">
              <Button
                size="xs"
                variant="subtle"
                color="red"
                disabled={entries.length <= 1}
                onClick={() =>
                  setEntries((prev) => prev.filter((_, j) => j !== i))
                }
              >
                Remove competitor
              </Button>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

const ExplorePlanComparisonsManage = () => {
  const { id: profileId } = useParams();
  const { data, isLoading, mutate } = useSWR(
    explorePlansApi.comparisonsList(profileId),
    fetcher,
    { revalidateOnFocus: false }
  );

  const {
    data: competitorsRaw,
    error: competitorsError,
    isLoading: competitorsLoading,
    mutate: mutateCompetitors,
  } = useSWR("cover-api/competitors-registry", fetchCompetitorsRegistry, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const apiList = useMemo(
    () => parseCompetitorsResponse(competitorsRaw),
    [competitorsRaw]
  );

  const rows = useMemo(() => parseComparisonsListResponse(data), [data]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [rows]
  );

  const previewColumns = useMemo(
    () => enrichMetaFromApi(buildTemplateFromRows(sortedRows), apiList),
    [sortedRows, apiList]
  );

  const ascPreviewTd = {
    background: "linear-gradient(180deg, #033f7c 0%, #0053a8 100%)",
    color: "#fff",
    fontWeight: 700,
    textAlign: "center",
  };

  const findCvById = (row, competitorId) =>
    (row.competitor_values || []).find(
      (cv) => Number(cv.competitor) === competitorId
    );

  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => ({
    coverage_label: "",
    asc360_value: "",
    asc360_is_winner: true,
    order: 0,
    competitor_values: [
      { competitor: "", label: "", value: "", is_negative: false },
    ],
  }));
  const [bulkRows, setBulkRows] = useState([]);
  const [competitorMeta, setCompetitorMeta] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkAddSelectNonce, setBulkAddSelectNonce] = useState(0);

  const bulkAddSelectData = useMemo(() => {
    const used = new Set(
      competitorMeta
        .map((m) => Number(m.competitor))
        .filter((n) => Number.isFinite(n) && n > 0)
    );
    return apiList
      .filter((item) => {
        const id = competitorItemId(item);
        return Number.isFinite(id) && id > 0 && !used.has(id);
      })
      .map((item) => ({
        value: String(competitorItemId(item)),
        label: competitorDisplayName(item),
      }));
  }, [apiList, competitorMeta]);

  const normalizeCompetitorValues = (list) =>
    (list || [])
      .map((cv) => ({
        competitor: Number(cv.competitor),
        value: cv.value || "",
        is_negative: !!cv.is_negative,
      }))
      .filter((cv) => Number.isFinite(cv.competitor) && cv.competitor > 0);

  const stripLabelsForApi = (list) =>
    (list || []).map(({ competitor, value, is_negative }) => ({
      competitor,
      value,
      is_negative,
    }));

  const openAdd = () => {
    setEditing(null);
    let meta = buildTemplateFromRows(sortedRows);
    if (!meta.length) meta = metaFromApiList(apiList);
    meta = enrichMetaFromApi(meta, apiList);
    const seed =
      meta.length > 0
        ? meta.map((m) => ({
            competitor: String(m.competitor || ""),
            label: m.label || "",
            value: "",
            is_negative: false,
          }))
        : [
            {
              competitor: "",
              label: "",
              value: "",
              is_negative: false,
            },
          ];
    setForm({
      coverage_label: "",
      asc360_value: "",
      asc360_is_winner: true,
      order: sortedRows.length,
      competitor_values: seed,
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm(toFormDynamic(row));
    setModalOpen(true);
  };

  const saveModal = async () => {
    setSaving(true);
    try {
      const body = {
        coverage_label: form.coverage_label,
        asc360_value: form.asc360_value,
        asc360_is_winner: !!form.asc360_is_winner,
        order: Number(form.order) || 0,
        competitor_values: normalizeCompetitorValues(
          stripLabelsForApi(form.competitor_values)
        ),
      };
      if (editing?.id) {
        await fetcher(
          explorePlansApi.comparisonRow(editing.id),
          "PATCH",
          body
        );
        successNotifications("Coverage row updated");
      } else {
        await fetcher(
          explorePlansApi.comparisonsList(profileId),
          "POST",
          body
        );
        successNotifications("Coverage row added");
      }
      setModalOpen(false);
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm("Delete this coverage row?")) return;
    try {
      await fetcher(explorePlansApi.comparisonRow(row.id), "DELETE");
      successNotifications("Row deleted");
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Delete failed", e);
    }
  };

  const normalizeBulk = (list) =>
    list.map((row, idx) => {
      const payload = {
        coverage_label: row.coverage_label || "",
        asc360_value: row.asc360_value || "",
        asc360_is_winner: !!row.asc360_is_winner,
        order: Number(row.order ?? idx),
        competitor_values: normalizeCompetitorValues(row.competitor_values),
      };
      if (row.id != null && row.id !== "") {
        payload.id = row.id;
      }
      return payload;
    });

  const openBulk = (opts) => {
    let meta = buildTemplateFromRows(sortedRows);
    if (!meta.length) meta = fallbackMetaFromRows(sortedRows);
    if (!meta.length && apiList.length) meta = metaFromApiList(apiList);
    meta = enrichMetaFromApi(meta, apiList);
    if (opts?.appendCompetitor) {
      const used = new Set(
        meta.map((x) => Number(x.competitor)).filter(Number.isFinite)
      );
      const pick = apiList.find((c) => {
        const cid = competitorItemId(c);
        return Number.isFinite(cid) && cid > 0 && !used.has(cid);
      });
      if (pick) {
        meta = [
          ...meta,
          {
            competitor: String(competitorItemId(pick)),
            label: competitorDisplayName(pick),
          },
        ];
      }
    }
    setCompetitorMeta(meta);
    if (sortedRows.length) {
      setBulkRows(sortedRows.map((r) => toFormForBulk(r, meta)));
    } else {
      setBulkRows([emptyCoverageRow(0, meta)]);
    }
    setBulkAddSelectNonce((n) => n + 1);
    setBulkOpen(true);
  };

  const setModalEntries = (fn) =>
    setForm((f) => ({
      ...f,
      competitor_values: fn(f.competitor_values || []),
    }));

  const patchBulkCellValue = (rowIdx, colIdx, value) => {
    setBulkRows((prev) => {
      const next = [...prev];
      const cv = [...(next[rowIdx].competitor_values || [])];
      cv[colIdx] = { ...cv[colIdx], value };
      next[rowIdx] = { ...next[rowIdx], competitor_values: cv };
      return next;
    });
  };

  const appendBulkCompetitorById = (competitorIdStr) => {
    if (!competitorIdStr) return;
    const idNum = Number(competitorIdStr);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    const hit = apiList.find((a) => competitorItemId(a) === idNum);
    const label = hit ? competitorDisplayName(hit) : "";
    const str = String(idNum);
    setCompetitorMeta((m) => {
      if (m.some((x) => Number(x.competitor) === idNum)) return m;
      return [...m, { competitor: str, label }];
    });
    setBulkRows((rows) =>
      rows.map((r) => ({
        ...r,
        competitor_values: [
          ...(r.competitor_values || []),
          { competitor: str, value: "", is_negative: false },
        ],
      }))
    );
    setBulkAddSelectNonce((n) => n + 1);
  };

  const removeBulkCompetitor = (idx) => {
    setCompetitorMeta((m) => m.filter((_, i) => i !== idx));
    setBulkRows((rows) =>
      rows.map((r) => ({
        ...r,
        competitor_values: (r.competitor_values || []).filter(
          (_, i) => i !== idx
        ),
      }))
    );
  };

  const addBulkCoverageRow = () => {
    setBulkRows((prev) => [
      ...prev,
      emptyCoverageRow(prev.length, competitorMeta),
    ]);
  };

  const saveBulk = async () => {
    setSaving(true);
    try {
      await fetcher(
        explorePlansApi.comparisonsBulk(profileId),
        "PUT",
        normalizeBulk(bulkRows)
      );
      successNotifications("Comparisons saved");
      setBulkOpen(false);
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Bulk save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const tableColCount = 3 + previewColumns.length;

  const competitorErrorText =
    competitorsError?.response?.data?.detail ||
    competitorsError?.response?.data?.message ||
    competitorsError?.message ||
    "The competitor directory could not be loaded. Confirm GET cover-api/competitors/ on the server.";

  return (
    <Stack gap="lg">
      <Paper radius="md" p={0} withBorder>
        <Box
          px="lg"
          py="md"
          style={{
            borderBottom: "1px solid #e2e8f0",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Box maw={560}>
              <Title order={4} c="#092c4c" fw={700}>
                Comparisons
              </Title>
              <Text size="sm" c="dimmed" mt={6}>
                Build coverage vs competitors; the table below matches what
                visitors see in the plan story.
              </Text>
            </Box>
            <Group gap="sm" wrap="wrap">
              <Button
                variant="outline"
                color="blue"
                size="sm"
                onClick={() => openBulk()}
              >
                Bulk edit
              </Button>
              <Button
                variant="outline"
                color="blue"
                size="sm"
                onClick={() => openBulk({ appendCompetitor: true })}
              >
                + Add competitor
              </Button>
              <Button
                variant="gradient"
                size="sm"
                gradient={{ from: "#0053A8", to: "#033F7C", deg: 90 }}
                onClick={openAdd}
              >
                + Add coverage
              </Button>
            </Group>
          </Group>
        </Box>

        {competitorsError ? (
          <Alert color="orange" title="Competitors API" mx="md" mt="md">
            <Text size="sm">{competitorErrorText}</Text>
            <Button
              mt="sm"
              size="xs"
              variant="light"
              onClick={() => mutateCompetitors()}
            >
              Retry
            </Button>
          </Alert>
        ) : null}

        {competitorsLoading && !apiList.length ? (
          <Text size="xs" c="dimmed" px="lg" py="xs">
            Loading competitor directory…
          </Text>
        ) : null}

        <div
          className={storyClasses.comparisonHeader}
          style={{ fontSize: 18 }}
        >
          FN - Plan Comparison
        </div>

        <Table.ScrollContainer minWidth={720}>
          <Table withColumnBorders highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr style={{ background: "#0c4d8d" }}>
                <Table.Th c="white" fw={700}>
                  Coverage
                </Table.Th>
                {previewColumns.map((col) => (
                  <Table.Th
                    key={String(col.competitor)}
                    c="white"
                    fw={700}
                    ta="center"
                  >
                    {col.label || `Competitor ${col.competitor}`}
                  </Table.Th>
                ))}
                <Table.Th c="white" fw={700} ta="center">
                  ASC360
                </Table.Th>
                <Table.Th c="white" fw={700} ta="center" w={120}>
                  Actions
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={tableColCount}>
                    <Center py="xl">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : sortedRows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={tableColCount}>
                    <Stack align="center" gap="sm" py="xl">
                      <Text c="dimmed" ta="center" fw={500}>
                        No coverage rows yet. Add rows with &quot;+ Add
                        coverage&quot; or &quot;Bulk edit&quot;.
                      </Text>
                      <Group>
                        <Button size="sm" variant="light" onClick={openAdd}>
                          + Add coverage
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          color="blue"
                          onClick={() => openBulk()}
                        >
                          Bulk edit
                        </Button>
                      </Group>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : (
                sortedRows.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td style={{ fontWeight: 500 }}>
                      {row.coverage_label || "—"}
                    </Table.Td>
                    {previewColumns.map((col) => {
                      const cid = Number(col.competitor);
                      const cv = findCvById(row, cid);
                      const cell = cv?.value || "—";
                      const neg = !!cv?.is_negative;
                      return (
                        <Table.Td
                          key={`${row.id}-${cid}`}
                          c="dimmed"
                          ta="center"
                          fz="sm"
                        >
                          {neg ? (
                            <Group gap={6} justify="center" wrap="nowrap">
                              <IconX
                                size={16}
                                color="var(--mantine-color-red-6)"
                              />
                              <Text span fz="sm">
                                {cell}
                              </Text>
                            </Group>
                          ) : (
                            cell
                          )}
                        </Table.Td>
                      );
                    })}
                    <Table.Td style={ascPreviewTd}>
                      {row.asc360_is_winner ? (
                        <Group gap={6} justify="center" wrap="nowrap">
                          <IconCheck size={16} color="#fff" />
                          <Text span c="#fff" fz="sm" fw={700}>
                            {row.asc360_value || "—"}
                          </Text>
                        </Group>
                      ) : (
                        row.asc360_value || "—"
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} justify="center" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit coverage row" : "Add coverage row"}
        size="xl"
        centered
      >
        <Paper withBorder p="md" radius="md">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
            <TextInput
              required
              label="Coverage label"
              placeholder="e.g. Helicopter Evacuation"
              value={form.coverage_label}
              onChange={(e) =>
                setForm((f) => ({ ...f, coverage_label: e.target.value }))
              }
            />
            <TextInput
              required
              label="ASC360 value"
              placeholder="e.g. Included Upto 4,000 m"
              value={form.asc360_value}
              onChange={(e) =>
                setForm((f) => ({ ...f, asc360_value: e.target.value }))
              }
            />
            <TextInput
              label="Order"
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm((f) => ({ ...f, order: e.target.value }))
              }
            />
            <Switch
              mt="lg"
              label="ASC360 wins"
              checked={!!form.asc360_is_winner}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  asc360_is_winner: e.currentTarget.checked,
                }))
              }
            />
          </SimpleGrid>
          <ModalCompetitorBlock
            entries={form.competitor_values}
            setEntries={setModalEntries}
            apiList={apiList}
            registryLoading={competitorsLoading}
            registryError={competitorsError}
            onRetryRegistry={() => mutateCompetitors()}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button loading={saving} onClick={saveModal}>
              Save
            </Button>
          </Group>
        </Paper>
      </Modal>

      <Modal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk edit comparisons"
        size="xl"
        centered
      >
        <Text size="sm" c="dimmed" mb="sm">
          Edit competitor columns once, then values per coverage row. Saving
          replaces all comparison rows for this profile.
        </Text>

        <Paper withBorder p="sm" radius="md" mb="md">
          <Text fw={600} size="sm" mb={4}>
            Competitor columns
          </Text>
          <Text size="sm" c="dimmed" mb="sm">
            Names and options come from the competitor directory API. Add or
            remove columns here; each coverage row below has a cell per column.
          </Text>
          {competitorsLoading && !apiList.length ? (
            <Group gap="xs" py="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading competitor directory…
              </Text>
            </Group>
          ) : null}
          {competitorsError && !apiList.length ? (
            <Alert color="orange" size="sm" mb="sm">
              <Text size="sm">
                Could not load competitors. Fix the API, then retry.
              </Text>
              <Button
                mt="xs"
                size="xs"
                variant="light"
                onClick={() => mutateCompetitors()}
              >
                Retry
              </Button>
            </Alert>
          ) : null}
          {!competitorsLoading && !competitorsError && !apiList.length ? (
            <Text size="sm" c="dimmed" mb="sm">
              No competitors returned from the API.
            </Text>
          ) : null}
          {apiList.length > 0 ? (
            <Stack gap="sm">
              {competitorMeta.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No competitor columns yet. Use &quot;Add competitor…&quot;
                  to pick from the directory.
                </Text>
              ) : (
                <Group gap="xs" align="center" wrap="wrap">
                  {competitorMeta.map((c, idx) => {
                    const idNum = Number(c.competitor);
                    const hit = apiList.find((a) => competitorItemId(a) === idNum);
                    const name =
                      hit
                        ? competitorDisplayName(hit)
                        : c.label ||
                          (c.competitor
                            ? `Competitor ${c.competitor}`
                            : "—");
                    return (
                      <Pill
                        key={`${c.competitor}-${idx}`}
                        size="md"
                        withRemoveButton
                        onRemove={() => removeBulkCompetitor(idx)}
                      >
                        {name}
                      </Pill>
                    );
                  })}
                </Group>
              )}
              <Select
                key={bulkAddSelectNonce}
                label="Add competitor column"
                placeholder={
                  bulkAddSelectData.length
                    ? "Choose from directory…"
                    : "All directory competitors added"
                }
                searchable
                clearable
                maw={360}
                data={bulkAddSelectData}
                value={null}
                disabled={!bulkAddSelectData.length}
                onChange={(v) => {
                  if (v) appendBulkCompetitorById(v);
                }}
              />
            </Stack>
          ) : null}
        </Paper>

        <Stack gap="md" style={{ maxHeight: 480, overflow: "auto" }}>
          {bulkRows.map((row, idx) => (
            <Paper key={idx} p="md" withBorder radius="md">
              <Group justify="space-between" mb="sm" wrap="nowrap">
                <Group gap="xs">
                  <Badge size="lg" variant="filled" color="blue">
                    {idx + 1}
                  </Badge>
                  <Text fw={600} size="sm">
                    Coverage row
                  </Text>
                </Group>
                <Button
                  size="xs"
                  variant="outline"
                  color="red"
                  leftSection={<IconX size={14} />}
                  onClick={() =>
                    setBulkRows((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
                <TextInput
                  required
                  label="Coverage label"
                  placeholder="e.g. Helicopter Evacuation"
                  value={row.coverage_label}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], coverage_label: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  required
                  label="ASC360 value"
                  placeholder="e.g. Included Upto 4,000 m"
                  value={row.asc360_value}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], asc360_value: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Order"
                  type="number"
                  value={row.order}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], order: v };
                      return next;
                    });
                  }}
                />
                <Switch
                  mt="lg"
                  label="ASC360 wins"
                  checked={!!row.asc360_is_winner}
                  onChange={(e) => {
                    const v = e.currentTarget.checked;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], asc360_is_winner: v };
                      return next;
                    });
                  }}
                />
              </SimpleGrid>
              {competitorMeta.length > 0 ? (
                <Box
                  mt="md"
                  p="sm"
                  bg="gray.0"
                  style={{ border: "1px solid #e2e8f0", borderRadius: 8 }}
                >
                  <Group gap={8} mb="sm">
                    <ThemeIcon size="sm" variant="light" color="blue" radius="md">
                      <IconUsers size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>
                      Competitor values
                    </Text>
                  </Group>
                  <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
                    {competitorMeta.map((meta, colIdx) => (
                      <TextInput
                        key={colIdx}
                        label={
                          meta.label || meta.competitor
                            ? `${meta.label || "Competitor"}${meta.competitor ? ` (id=${meta.competitor})` : ""}`
                            : `Competitor ${colIdx + 1}`
                        }
                        placeholder="Their coverage"
                        value={row.competitor_values[colIdx]?.value || ""}
                        onChange={(e) =>
                          patchBulkCellValue(idx, colIdx, e.target.value)
                        }
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              ) : null}
            </Paper>
          ))}
        </Stack>
        <Group justify="space-between" mt="sm" wrap="wrap">
          <Button size="xs" variant="light" onClick={addBulkCoverageRow}>
            + Add coverage row
          </Button>
        </Group>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setBulkOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            gradient={{ from: "#0053A8", to: "#033F7C", deg: 90 }}
            loading={saving}
            onClick={saveBulk}
          >
            Save bulk
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default ExplorePlanComparisonsManage;
